import axios from 'axios';
import {
  withdrawalPreviewSchema,
  type ZeroXWithdrawalPreview,
  type ZeroXWithdrawalResult,
} from '../../schemas/zeroXWithdrawal';

export type ZeroXWithdrawalMessage = {
  severity: 'error' | 'warning' | 'info';
  key: string;
  values?: Record<string, string>;
};

export type ZeroXWithdrawalPhase =
  | { name: 'editing' | 'previewing' }
  | { name: 'reviewing' | 'confirming' | 'uncertain' | 'expired'; quote: ZeroXWithdrawalPreview }
  | { name: 'done'; result: ZeroXWithdrawalResult; quote: ZeroXWithdrawalPreview };

export type ZeroXWithdrawalFlowState = {
  phase: ZeroXWithdrawalPhase;
  message: ZeroXWithdrawalMessage | null;
};

export const INITIAL_WITHDRAWAL_STATE: ZeroXWithdrawalFlowState = {
  phase: { name: 'editing' },
  message: null,
};

const ERROR_KEYS: Record<string, string> = {
  INSUFFICIENT_BALANCE: 'insufficientFunds',
  LIMIT_EXCEEDED: 'zeroXWithdrawalLimit',
  BELOW_MINIMUM: 'zeroXWithdrawalMinimum',
  INVALID_DESTINATION: 'invalidWalletAddress',
  TAG_REQUIRED: 'zeroXWithdrawalTagRequired',
  TAG_NOT_ALLOWED: 'zeroXWithdrawalTagNotAllowed',
  CURRENCY_UNAVAILABLE: 'zeroXWithdrawalUnavailable',
  WITHDRAWAL_TEMPORARILY_UNAVAILABLE: 'zeroXWithdrawalAmountUnavailable',
};

const getErrorResponse = (error: unknown) => {
  if (axios.isAxiosError<Record<string, unknown>>(error)) {
    return { status: error.response?.status, data: error.response?.data };
  }

  return { status: undefined, data: undefined };
};

export const getWithdrawalErrorMessage = (error: unknown, currency: string): ZeroXWithdrawalMessage => {
  const { data } = getErrorResponse(error);
  const code = typeof data?.code === 'string' ? data.code : '';
  const minimum = data?.minimumWithdrawalAmount;

  if (code === 'BELOW_MINIMUM' && typeof minimum === 'string') {
    return { severity: 'error', key: 'zeroXMinimumAssetAmount', values: { minimum, currency } };
  }

  return { severity: 'error', key: ERROR_KEYS[code] ?? 'zeroXWithdrawalUnavailable' };
};

export const resolveConfirmationFailure = (error: unknown, quote: ZeroXWithdrawalPreview): ZeroXWithdrawalFlowState => {
  const { status, data } = getErrorResponse(error);

  if (status === 409 && data?.code === 'WITHDRAWAL_PREVIEW_CHANGED') {
    const fresh = withdrawalPreviewSchema.safeParse(data.preview);

    if (fresh.success) {
      return {
        phase: { name: 'reviewing', quote: fresh.data },
        message: { severity: 'warning', key: 'zeroXQuoteChanged' },
      };
    }
  }

  const definitelyRejected =
    status === 400 || status === 422 || (status === 409 && data?.code === 'WITHDRAWAL_PREVIEW_EXPIRED');

  if (definitelyRejected) {
    if (data?.code === 'WITHDRAWAL_PREVIEW_EXPIRED' || data?.code === 'INVALID_CONFIRMATION_TOKEN') {
      return {
        phase: { name: 'expired', quote },
        message: { severity: 'warning', key: 'zeroXQuoteExpired' },
      };
    }

    return { phase: { name: 'editing' }, message: getWithdrawalErrorMessage(error, quote.currency) };
  }

  // The request may have committed. Preserve its token until the wallet resolves it.
  return {
    phase: { name: 'uncertain', quote },
    message: { severity: 'warning', key: 'zeroXRequestUncertain' },
  };
};
